<template>
  <v-container fluid class="pa-2">
    <v-toolbar flat dense class="mb-2">
      <v-toolbar-title class="text-subtitle-1 font-weight-medium d-flex">
        <v-icon left small class="mr-2">mdi-view-dashboard</v-icon>
        Tablero de Documentos
      </v-toolbar-title>
      <v-spacer />
      <v-text-field
        v-model="issueDate"
        type="date"
        hide-details
        outlined
        class="mr-2"
        style="max-width: 170px"
        @change="loadDocs"
      />

      <v-text-field
        v-model="search"
        hide-details
        outlined
        clearable
        placeholder="Buscar cliente o documento..."
        prepend-inner-icon="mdi-magnify"
        style="max-width: 250px"
        class="mr-2"
        @input="debouncedFilter"
      />
      <v-btn large outlined color="success" class="mr-2" :to="{ name: 'Invoices' }"
        ><v-icon left small>mdi-table</v-icon> Tabla</v-btn
      >
      <v-btn large color="success" :to="{ name: 'InvoicePOS' }"
        ><v-icon left small>mdi-cash-register</v-icon> POS</v-btn
      >
    </v-toolbar>

    <v-row no-gutters class="mb-2">
      <v-col cols="12" class="d-flex flex-wrap">
        <v-chip large class="ma-1" label color="primary" dark>
          <v-icon small left>mdi-text-box-multiple-outline</v-icon>
          {{ filtered.length }} documentos
        </v-chip>
        <v-chip large class="ma-1" label>
          <v-icon small left color="green">mdi-cash</v-icon>
          Total: {{ formatCurrency(totalValue) }}
        </v-chip>
        <v-chip large label class="ma-1">
          <v-icon small left>mdi-chart-line</v-icon>
          Promedio: {{ formatCurrency(avgValue) }}
        </v-chip>
        <v-spacer />
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon large v-on="on" @click="loadDocs" :loading="loading">
              <v-icon large>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Actualizar documentos</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <div class="tickets-grid">
      <v-row class="groups-wrapper" dense>
        <v-col
          v-for="g in groupedDocs"
          :key="g.partyId"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="2"
          class="d-flex"
        >
          <v-card outlined>
            <v-card-title class="py-1 pr-2">
              <div class="d-flex align-center">
                <v-icon x-small class="mr-1">mdi-account</v-icon>
                <span class="group-name" :title="g.partyName">{{
                  truncate(g.partyName, 40)
                }}</span>
              </div>
              <v-spacer />
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-chip small outlined label class="mr-1" v-on="on">
                    <v-icon x-small left>mdi-receipt</v-icon>
                    {{ g.docs.length }}
                  </v-chip>
                </template>
                <span
                  >{{ g.docs.length }} comando{{ g.docs.length !== 1 ? "s" : "" }}</span
                >
              </v-tooltip>
              <v-chip small label :color="g.total > 0 ? 'success' : 'error'" dark>
                {{ formatCurrency(g.total) }}
              </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text class="py-2 px-2">
              <div class="docs-list">
                <div v-for="d in g.docs" :key="d.doc_number" class="doc-outer">
                  <div class="doc-row" :class="d.cufe ? 'ok' : 'pending'">
                    <div class="left">
                      <v-btn
                        icon
                        x-small
                        class="mr-1 toggle-btn"
                        @click.stop="toggleDoc(d)"
                        ><v-icon x-small>{{
                          expanded[d.doc_number]
                            ? "mdi-chevron-down"
                            : "mdi-chevron-right"
                        }}</v-icon></v-btn
                      >
                      <v-chip
                        x-small
                        dark
                        :color="typeColor(d.doc_type)"
                        class="mr-1 type-chip"
                        >{{ d.doc_type }}</v-chip
                      >
                      <span
                        class="doc-number"
                        :title="d.PartyName"
                        @click.stop="openDoc(d)"
                        >{{ d.doc_number }}</span
                      >
                      <span class="time ml-2">{{ timePart(d.issuedate) }}</span>
                      <span v-if="d.invoice" class="invoice ml-2">{{ d.invoice }}</span>
                      <v-chip v-if="d.cufe_n" x-small outlined color="info" class="ml-1"
                        >NC</v-chip
                      >
                    </div>
                    <div class="right">
                      <v-icon
                        x-small
                        class="mr-1"
                        :color="d.cufe ? 'success' : 'orange'"
                        >{{ d.cufe ? "mdi-check-circle" : "mdi-timer-sand" }}</v-icon
                      >
                      <span class="value">{{ formatCurrency(d.doc_value) }}</span>
                    </div>
                  </div>
                  <v-expand-transition>
                    <div v-show="expanded[d.doc_number]" class="items-wrapper">
                      <div v-if="loadingItems[d.doc_number]" class="items-loading">
                        <v-progress-circular
                          indeterminate
                          size="16"
                          color="primary"
                          class="mr-1"
                        />
                        Cargando ítems...
                      </div>
                      <v-list
                        v-else-if="
                          docItems[d.doc_number] && docItems[d.doc_number].length
                        "
                        dense
                        class="items-v-list"
                      >
                        <v-list-item
                          v-for="(it, idx) in docItems[d.doc_number]"
                          :key="it.id || it.item_code || idx"
                          class="item-row"
                        >
                          <v-list-item-content>
                            <v-list-item-title class="item-line">{{
                              it.item_concept || it.description || "Item"
                            }}</v-list-item-title>
                            <v-list-item-subtitle class="item-sub"
                              >{{ qty(it) }} x
                              {{ formatCurrency(price(it)) }}</v-list-item-subtitle
                            >
                          </v-list-item-content>
                          <v-list-item-action
                            ><span class="item-total">{{
                              formatCurrency(qty(it) * price(it))
                            }}</span></v-list-item-action
                          >
                        </v-list-item>
                      </v-list>
                      <div v-else class="items-empty grey--text text--darken-1">
                        Sin ítems
                      </div>
                    </div>
                  </v-expand-transition>
                </div>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn
                small
                color="red"
                class="ml-1"
                outlined
                @click.stop="confirmCancel(g)"
                :disabled="g.docs.some((d) => d.cufe)"
              >
                <v-icon left x-small>mdi-delete-outline</v-icon> Anular
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                small
                color="deep-purple"
                class="ml-1"
                outlined
                @click.stop="prepareCheckout(g)"
                :disabled="!canPrepareCheckout(g)"
              >
                <v-icon left x-small>mdi-cash-register</v-icon> Cobrar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <div v-if="!loading && filtered.length === 0" class="empty">
        <v-icon x-large color="grey lighten-1">mdi-note-multiple-outline</v-icon>
        <div class="mt-3 text-h6 grey--text text--darken-1">Sin documentos</div>
        <div class="mt-2 text-body-2 grey--text">{{ getEmptyMessage() }}</div>
        <v-btn v-if="search" text color="primary" class="mt-3" @click="clearSearch">
          <v-icon left>mdi-close</v-icon>
          Limpiar búsqueda
        </v-btn>
      </div>
      <div v-if="loading" class="overlay">
        <v-card class="pa-4 text-center">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="mt-3 text-body-1">Cargando documentos...</div>
        </v-card>
      </div>
    </div>
    <noteViewer
      :item="viewerItem"
      :dialog="viewerDialog"
      :newInvoice="newInvoiceFlag"
      @close="closeViewer"
      @refresh="loadDocs"
    />
    <CheckoutComponent
      v-if="payment_dialogo"
      :visible.sync="payment_dialogo"
      :totalAmount="checkoutTotals.total"
      :unpaidAmount="checkoutTotals.unpaid"
      :vendors="vendors"
      :paymentMethods="FP"
      :idList="checkoutIdList"
      :municipalities="[]"
      :companyData="sellerInfo || companyInfo || { city_code: '' }"
      :clientData="checkoutDraft ? checkoutDraft.buyer : {}"
      :validations="checkoutValidations"
      @doc-type-changed="handleDocTypeChanged"
      @validation-updated="handleValidationUpdated"
      @process-payment="handleProcessPayment"
      @open-client-dialog="handleOpenClientDialog"
      @show-message="handleShowMessage"
    />
    <v-dialog v-model="cancelDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left color="red">mdi-alert-circle</v-icon>
          Anular comanda
        </v-card-title>
        <v-card-text>
          Se anularán <strong>{{ cancelGroup ? cancelGroup.docs.length : 0 }}</strong>
          documento(s) de <strong>{{ cancelGroup ? cancelGroup.partyName : "" }}</strong>
          por <strong>{{ formatCurrency(cancelGroup ? cancelGroup.total : 0) }}</strong>.
          <br /><br />
          Esta acción restaurará el inventario y no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDialog = false">Cerrar</v-btn>
          <v-btn color="red" dark :loading="cancelling" @click="cancelGroupDocs">
            <v-icon left small>mdi-delete-outline</v-icon> Anular
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top>
      {{ snackbar.text }}
    </v-snackbar>

    <v-dialog v-model="clientEditDialog" max-width="600px" persistent>
      <v-card>
        <v-toolbar dense flat color="primary" dark>
          <v-toolbar-title>Editar Cliente</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="clientEditDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-form ref="clientForm">
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="buyerEdit.typeid"
                  :items="typeIdOptions"
                  label="Tipo"
                  dense
                  outlined
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="buyerEdit.PartyIdentification"
                  label="Documento"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="buyerEdit.PartyName"
                  label="Nombre / Razón Social"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="buyerEdit.client_email"
                  label="Email"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="buyerEdit.client_phone"
                  label="Teléfono"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="buyerEdit.AddressLine"
                  label="Dirección"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="buyerEdit.CityName"
                  label="Ciudad"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="clientEditDialog = false">Cancelar</v-btn>
          <v-btn color="success" :disabled="!buyerEdit.PartyName" @click="saveClientEdit"
            >Guardar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { webserver, getToday, formatCurrency } from "@/services/webserver.js";
import noteViewer from "@/components/doc_viewer.vue";
import CheckoutComponent from "@/components/checkout.vue";

export default {
  name: "InvoicesBoard",
  components: { noteViewer, CheckoutComponent },
  data() {
    return {
      issueDate: getToday(),
      docs: [],
      filtered: [],
      search: "",
      loading: false,
      viewerDialog: false,
      viewerItem: { document: null, items: [], payments: [] },
      newInvoiceFlag: false,
      docItems: {},
      loadingItems: {},
      expanded: {},
      companyInfo: null,
      sellerInfo: null,
      totalDocuments: 0,
      useNewEndpoint: true, // get_documents_by_day del backend filtra por doc_reference IS NULL → solo CMs pendientes
      cobrandoMesaId: null, // ID de la mesa que se está cobrando (preservado antes de la conversión a CONSUMIDOR FINAL)
      // Checkout integration
      payment_dialogo: false,
      checkoutDraft: null,
      checkoutTotals: { total: 0, unpaid: 0 },
      checkoutValidations: [
        { status: false, label: "Nombre del cliente requerido" },
        { status: true, label: "Correo electrónico es opcional" },
        { status: false, label: "Documento de identificación requerido" },
        { status: false, label: "Tipo de documento requerido" },
        { status: true, label: "Vendedor requerido" },
        { status: false, label: "Requiere Factura Electrónica" },
        { status: false, label: "Valor" },
        { status: false, label: "Ciudad requerida" },
      ],
      postingInvoice: false,
      vendors: [], // TODO: cargar si aplica
      FP: [], // Formas de pago (se pueden cargar luego)
      checkoutIdList: [
        { code: "FE", name: "Factura Electrónica" },
        { code: "TK", name: "Ticket" },
        { code: "FS", name: "Factura Simplificada" },
        { code: "FC", name: "Factura Completa" },
      ],
      clientEditDialog: false,
      buyerEdit: {
        PartyIdentification: "",
        PartyName: "",
        typeid: "CC",
        AddressLine: "",
        client_email: "",
        client_phone: "",
        CityName: "",
      },
      typeIdOptions: ["CC", "NIT", "CE", "TI", "PP", "DNI", "PAS"],
      docs_reference: [],
      searchDebounce: null,
      cancelDialog: false,
      cancelGroup: null,
      cancelling: false,
      snackbar: { show: false, text: "", color: "success" },
    };
  },
  computed: {
    totalValue() {
      return this.filtered.reduce(
        (s, d) =>
          s +
          (d.doc_type === "NC"
            ? -parseFloat(d.doc_value || 0)
            : parseFloat(d.doc_value || 0)),
        0
      );
    },
    avgValue() {
      return this.filtered.length ? this.totalValue / this.filtered.length : 0;
    },
    groupedDocs() {
      const map = {};
      this.filtered.forEach((d) => {
        const k = d.PartyIdentification || "SIN_ID";
        if (!map[k])
          map[k] = { partyId: k, partyName: d.PartyName || k, docs: [], total: 0 };
        map[k].docs.push(d);
        const val = parseFloat(d.doc_value || 0);
        map[k].total += d.doc_type === "NC" ? -val : val;
      });
      return Object.values(map).sort((a, b) => b.total - a.total);
    },
  },
  mounted() {
    this.loadDocs();
    // Cargar formas de pago igual que en InvoiceE (this.FP = window.settings.FP || [])
    try {
      this.FP = window.settings && window.settings.FP ? [...window.settings.FP] : [];
    } catch (e) {
      this.FP = [];
    }
    this.get_vendors();
  },
  methods: {
    formatCurrency,
    debouncedFilter() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.filterDocs();
      }, 300);
    },
    clearSearch() {
      this.search = "";
      this.filterDocs();
    },
    getEmptyMessage() {
      if (this.search) {
        return `No se encontraron documentos para "${this.search}"`;
      }
      return "No hay documentos para la fecha seleccionada";
    },
    qty(it) {
      return parseFloat(it.item_quantity || it.qty || it.quantity || it.cantidad || 0);
    },
    price(it) {
      return parseFloat(
        it.item_price || it.price || it.unit_price || it.price_unit || it.unitvalue || 0
      );
    },
    loadDocs() {
      this.loading = true;
      if (this.useNewEndpoint) {
        const qry = {
          store: window.store.store_id,
          date: this.issueDate,
          doc_type: "CM",
        };
        const limited = this.$store.getters.profile.user_access === "USUARIO";
        const userEmail = this.$store.state.profile.email;
        console.log("[Board] Consulta get_documents_by_day:", qry);
        webserver("get_documents_by_day", qry, (resp) => {
          console.log("[Board] Respuesta backend:", resp);
          this.loading = false;
          // Resultado vacío del backend ({error: "No documents found..."}) — no es error real
          if (resp && typeof resp.error === "string") {
            console.log("[Board] Sin documentos PENDIENTES para", this.issueDate, "—", resp.error);
            this.docs = [];
            this.totalDocuments = 0;
            this.filterDocs();
            // Diagnóstico: ver si hay CMs hoy ya cobrados o no hay nada en absoluto
            this.diagnosticarCMsDelDia();
            return;
          }
          // Error real (wrapper webserver: timeout, status != 200, etc.)
          if (resp && resp.error === true) {
            console.warn("[Board] Error de red:", resp.message);
            this.docs = [];
            this.filterDocs();
            return;
          }
          if (resp && resp.success) {
            let docs = resp.documents || [];
            console.log(`[Board] Documentos recibidos: ${docs.length}`);
            if (limited)
              docs = docs.filter((r) => r.document && r.document.userId === userEmail);
            this.docItems = {};
            this.docs = docs
              .map((r) => {
                const doc = r.document || {};
                const buyer = r.buyer || {};
                if (r.items) this.$set(this.docItems, doc.doc_number, r.items);
                return {
                  ...doc,
                  PartyIdentification:
                    doc.PartyIdentification || buyer.PartyIdentification || "",
                  PartyName: doc.PartyName || buyer.PartyName || "",
                };
              })
              .filter((d) => d.doc_status !== "ANULADA");
            this.companyInfo = resp.company;
            this.sellerInfo = resp.seller;
            this.totalDocuments = resp.total_documents || this.docs.length;
          } else {
            console.warn("[Board] Respuesta sin success:true. Estructura:", resp);
            this.docs = [];
          }
          this.filterDocs();
        });
      } else {
        const qry = {
          store: window.store.store_id,
          table: "docs",
          filters: [
            { field: "doc_type", operator: " IN ", value: "'CM'" },
            { field: "DATE(issuedate)", operator: "=", value: this.issueDate },
            { field: "store", operator: "=", value: window.store.store_id },
          ],
        };
        if (this.$store.getters.profile.user_access === "USUARIO")
          qry.filters.push({
            field: "userId",
            operator: "=",
            value: this.$store.state.profile.email,
          });
        console.log("[Board OLD] Consulta get_dataTable docs:", qry);
        webserver("get_dataTable", qry, (data) => {
          console.log("[Board OLD] Respuesta:", data);
          this.docs = Array.isArray(data) ? data : [];
          this.loading = false;

          // Pre-cargar los items de cada CM para que el botón "Cobrar" pueda activarse.
          // Sin esto, canPrepareCheckout siempre devuelve false porque docItems está vacío.
          if (this.docs.length > 0) {
            console.log(
              `[Board OLD] Pre-cargando items de ${this.docs.length} documentos...`
            );
            this.docs.forEach((d) => {
              if (d.doc_number && !this.docItems[d.doc_number]) {
                this.loadItems(d);
              }
            });
          }

          // Fallback diagnóstico: si la consulta filtrada no trae nada,
          // hacer una consulta amplia para ver qué hay realmente en docs hoy
          if (this.docs.length === 0) {
            console.warn(
              "[Board OLD] Sin CMs hoy. Lanzando consulta diagnóstica AMPLIA (sin filtros doc_type ni fecha)..."
            );
            webserver(
              "get_dataTable",
              {
                store: window.store.store_id,
                table: "docs",
                filters: [
                  {
                    field: "store",
                    operator: "=",
                    value: window.store.store_id,
                  },
                ],
                limit: 10,
              },
              (allDocs) => {
                console.log(
                  "[Diag] Últimos 10 docs (cualquier tipo) de la tienda:",
                  allDocs
                );
                if (Array.isArray(allDocs)) {
                  const cms = allDocs.filter((d) => d.doc_type === "CM");
                  console.log(
                    `[Diag] De esos, ${cms.length} son CM. doc_types presentes:`,
                    [...new Set(allDocs.map((d) => d.doc_type))]
                  );
                  if (cms.length > 0) {
                    console.log(
                      "[Diag] Primer CM encontrado:",
                      cms[0],
                      "→ issuedate:",
                      cms[0].issuedate
                    );
                  }
                }
              }
            );
          }
          this.filterDocs();
        });
      }
    },
    filterDocs() {
      let list = [...this.docs];
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(
          (d) =>
            (d.doc_number || "").toLowerCase().includes(q) ||
            (d.PartyName || "").toLowerCase().includes(q) ||
            (d.invoice || "").toLowerCase().includes(q)
        );
      }
      list.sort((a, b) => new Date(b.issuedate) - new Date(a.issuedate));
      this.filtered = list;
    },
    typeColor(t) {
      switch (t) {
        case "TK":
          return "blue";
        case "FS":
          return "indigo";
        case "FC":
          return "primary";
        case "FE":
          return "deep-purple";
        case "NC":
          return "orange darken-2";
        case "ND":
          return "pink darken-2";
        default:
          return "grey";
      }
    },
    truncate(s, l) {
      if (!s) return "";
      return s.length > l ? s.slice(0, l - 1) + "…" : s;
    },
    timePart(dt) {
      if (!dt) return "";
      const p = dt.split(" ");
      return p[1] ? p[1].slice(0, 5) : "";
    },
    openDoc(d) {
      const payload = {
        store: d.store,
        issueDate: d.issuedate.split(" ")[0],
        did: d.doc_number,
      };
      webserver("get_invoice_fe", payload, (data) => {
        this.viewerItem = data;
        this.viewerDialog = true;
      });
    },
    toggleDoc(d) {
      const id = d.doc_number;
      this.$set(this.expanded, id, !this.expanded[id]);
      if (
        !this.useNewEndpoint &&
        this.expanded[id] &&
        !this.docItems[id] &&
        !this.loadingItems[id]
      )
        this.loadItems(d);
    },
    loadItems(d) {
      // Cargar items lazy desde el backend (necesario para activar el botón "Cobrar")
      const id = d.doc_number;
      this.$set(this.loadingItems, id, true);
      const payload = {
        store: d.store,
        issueDate: d.issuedate.split(" ")[0],
        did: d.doc_number,
      };
      webserver("get_invoice_fe", payload, (data) => {
        const items = (data && data.items) || [];
        this.$set(this.docItems, id, items);
        this.$delete(this.loadingItems, id);
      });
    },
    confirmCancel(group) {
      this.cancelGroup = group;
      this.cancelDialog = true;
    },
    cancelGroupDocs() {
      if (!this.cancelGroup) return;
      this.cancelling = true;
      const docs = this.cancelGroup.docs;
      const groupPartyId = this.cancelGroup.partyId;
      let completed = 0;
      let success = 0;
      let failed = 0;
      const total = docs.length;
      const errores = [];

      docs.forEach((d) => {
        const qry = {
          store: d.store || window.store.store_id,
          did: d.doc_number,
        };
        webserver("comanda_cancel", qry, (resp) => {
          completed++;
          // Validar respuesta: webserver puede devolver {error: true} (red) o
          // {error: "string"} (rechazo backend) o {success: true, result: ...}
          if (resp && resp.success === true) {
            success++;
          } else {
            failed++;
            const msg =
              (resp && resp.error) ||
              (resp && resp.message) ||
              "respuesta inválida";
            errores.push(`${d.doc_number}: ${msg}`);
            console.warn(`[Anular] Falló ${d.doc_number}:`, resp);
          }

          if (completed >= total) {
            this.cancelling = false;
            this.cancelDialog = false;
            this.cancelGroup = null;

            // Liberar la mesa SOLO si todas se anularon correctamente y era una mesa
            if (success === total && groupPartyId) {
              this.afterMesaCobrada(groupPartyId);
            }

            // Snackbar diferenciado por resultado
            if (failed === 0) {
              this.snackbar = {
                show: true,
                text: `${total} documento(s) anulado(s) correctamente`,
                color: "success",
              };
            } else if (success === 0) {
              this.snackbar = {
                show: true,
                text: `Ninguna se pudo anular. ${errores[0] || ""}`,
                color: "error",
              };
            } else {
              this.snackbar = {
                show: true,
                text: `${success} anulada(s), ${failed} fallaron — revisar consola`,
                color: "warning",
              };
            }
            this.loadDocs();
          }
        });
      });
    },
    generateInvoiceJSON(group) {
      // Payload para CREACIÓN de nueva factura (sin doc_number en doc ni en items)
      console.log("GENERATE INVOICE JSON FOR GROUP:", group.docs);
      const seller = this.sellerInfo || this.$store.state.seller || {};
      const profile = this.$store.state.profile || {};
      const firstDoc = group.docs[0] || {};
      const storeCode =
        firstDoc.store ||
        seller.store_code ||
        (window.store && window.store.store_id) ||
        "";
      // Buyer extendido (rellenos básicos si no existen aún)
      // Si es CONSUMIDOR FINAL o mesa_, asegurar que use el número genérico 2222222222
      let partyId = group.partyId;
      let partyName = group.partyName;

      // Detectar si es una mesa sin cliente personalizado
      const isMesaGeneric =
        partyId && partyId.toString().toLowerCase().startsWith("mesa_");

      if (
        group.partyName === "CONSUMIDOR FINAL" ||
        partyId === "" ||
        !partyId ||
        isMesaGeneric
      ) {
        partyId = "222222222222";
        // Si es mesa genérica, también cambiar el nombre
        if (isMesaGeneric) {
          partyName = "CONSUMIDOR FINAL";
          console.log(
            `[InvoicesBoard] Convirtiendo ${group.partyId} a CONSUMIDOR FINAL con ID genérico`
          );
        }
      }

      const buyer = {
        PartyIdentification: partyId,
        PartyName: partyName === "" || !partyName ? "CONSUMIDOR FINAL" : partyName,
        typeid: firstDoc.typeid || "CC",
        AddressLine: firstDoc.AddressLine || "",
        client_email: firstDoc.client_email || "",
        client_phone: firstDoc.client_phone || "",
        CityName: firstDoc.CityName || null,
      };
      // Consolidar y transformar items (sin doc_number)
      const items = [];
      group.docs.forEach((d) => {
        const list = this.docItems[d.doc_number] || [];
        list.forEach((it) => {
          const cantidad = this.qty(it);
          const precio = parseFloat(
            it.precio || it.price || it.item_price || this.price(it) || 0
          );
          const taxPercent = parseFloat(it.tax || it.iva || 0) || 0; // porcentaje
          // Usar valores existentes si vienen, NO recalcular
          let item_tax = it.item_tax !== undefined ? parseFloat(it.item_tax) : 0;
          let price_amount =
            it.price_amount !== undefined ? parseFloat(it.price_amount) : 0;

          // Solo calcular si NO vienen los valores
          if (item_tax === 0 && price_amount === 0) {
            if (taxPercent > 0) {
              const baseUnit = +(precio / (1 + taxPercent / 100)).toFixed(4);
              price_amount = baseUnit;
              item_tax = +(precio - baseUnit).toFixed(4);
            } else {
              price_amount = precio;
              item_tax = 0;
            }
          }
          items.push({
            id_pos: storeCode,
            sku: it.sku || it.item_code || it.code || "",
            ean: it.ean || it.barcode || it.sku || it.item_code || "",
            nombre: it.nombre || it.item_concept || it.description || "Item",
            categoria: it.categoria || it.category_code || "",
            grupo: it.grupo || null,
            marca: it.marca || it.brand || "",
            refe: it.refe || it.reference || it.item_reference || "",
            color: it.color || "",
            talla: it.talla || "",
            cantidad,
            costo: it.costo || it.cost || "0.0000",
            precio, // precio con IVA
            precioB: it.precioB || "0.0000",
            precioC: it.precioC || "0.0000",
            descuento: it.descuento || it.discount || "0.0000",
            tax: (taxPercent || 0).toFixed(4),
            id_proveedor: it.id_proveedor || it.provider_id || "0001",
            store_name: seller.PartyName || "",
            category: it.category || it.categoria || "",
            groupName: it.groupName || null,
            brand: it.brand || it.marca || null,
            colorName: it.colorName || null,
            note: it.item_note || it.note || "",
            isExpanded: false,
            discount: it.discount || 0,
            price_amount: +price_amount.toFixed(2), // base unit
            item_tax: +item_tax.toFixed(2), // iva unit
          });
        });
      });
      // Totales del documento (valor bruto con IVA)
      const doc_value = items.reduce((s, it) => s + it.precio * it.cantidad, 0);
      // Objeto doc (sin doc_number)
      const doc = {
        doc_type: "FE", // tipo destino deseado
        doc_value: Math.round(doc_value),
        store: storeCode,
        userId: profile.email || firstDoc.userId || "",
        vendor: profile.email || firstDoc.vendor || "",
      };
      // Pago simple default (EFECTIVO por total)
      const payments = [
        {
          payment_concept: "EFECTIVO",
          payment_code: "01",
          payment_reference: "EFECTIVO",
          payment_value: Math.round(doc_value),
        },
      ];
      const invoiceDraft = {
        buyer,
        doc,
        items,
        payments,
        seller: {
          ...seller,
        },
        store: storeCode,
      };
      // eslint-disable-next-line no-console
      console.log("INVOICE_CREATE_PAYLOAD", invoiceDraft);
    },
    get_vendors() {
      if (!window.store || !window.store.store_id) return;
      const qry = { store: window.store.store_id };
      webserver("vendors", qry, (data) => {
        if (Array.isArray(data)) {
          data.forEach((v) => {
            if (v.nombre) v.nombre = v.nombre.toUpperCase();
          });
          this.vendors = data;
        }
      });
    },
    canPrepareCheckout(group) {
      // Requerimos que todos los ítems de las comandas del grupo estén cargados
      return group.docs.every(
        (d) => this.docItems[d.doc_number] && this.docItems[d.doc_number].length
      );
    },
    prepareCheckout(group) {
      if (!this.canPrepareCheckout(group)) {
        this.$store.commit("setSnackbar", {
          text: "Cargue los ítems antes de cobrar",
          color: "warning",
        });
        return;
      }
      // Asegurar estructura de validaciones
      if (!this.checkoutValidations || this.checkoutValidations.length < 8) {
        this.resetCheckoutValidations();
      }
      const seller = this.sellerInfo || this.$store.state.seller || {};
      const profile = this.$store.state.profile || {};
      const firstDoc = group.docs[0] || {};
      const storeCode =
        firstDoc.store ||
        seller.store_code ||
        (window.store && window.store.store_id) ||
        "";
      // Reutilizar parte de generateInvoiceJSON pero sin pagos iniciales
      // Si es CONSUMIDOR FINAL o mesa_, asegurar que use el número genérico 2222222222
      let partyId = group.partyId;
      let partyName = group.partyName;

      // Detectar si es una mesa sin cliente personalizado
      const isMesaGeneric =
        partyId && partyId.toString().toLowerCase().startsWith("mesa_");

      // Preservar el ID de mesa ORIGINAL para liberarla en localStorage tras cobrar
      this.cobrandoMesaId = isMesaGeneric ? group.partyId : null;

      if (
        group.partyName === "CONSUMIDOR FINAL" ||
        partyId === "" ||
        !partyId ||
        isMesaGeneric
      ) {
        partyId = "222222222222";
        // Si es mesa genérica, también cambiar el nombre
        if (isMesaGeneric) {
          partyName = "CONSUMIDOR FINAL";
          console.log(
            `[InvoicesBoard] Convirtiendo ${group.partyId} a CONSUMIDOR FINAL con ID genérico`
          );
        }
      }

      const buyer = {
        PartyIdentification: partyId,
        PartyName: partyName === "" || !partyName ? "CONSUMIDOR FINAL" : partyName,
        typeid: firstDoc.typeid || "CC",
        AddressLine: firstDoc.AddressLine || "",
        client_email: firstDoc.client_email || "",
        client_phone: firstDoc.client_phone || "",
        CityName: firstDoc.CityName || null,
      };
      const items = [];
      group.docs.forEach((d) => {
        const list = this.docItems[d.doc_number] || [];
        list.forEach((it) => {
          const cantidad = this.qty(it);
          const precio = parseFloat(
            it.precio || it.price || it.item_price || this.price(it) || 0
          );
          const taxPercent = parseFloat(it.tax || it.iva || 0) || 0;
          // Usar valores existentes si vienen, NO recalcular
          let item_tax = it.item_tax !== undefined ? parseFloat(it.item_tax) : 0;
          let price_amount =
            it.price_amount !== undefined ? parseFloat(it.price_amount) : 0;

          // Solo calcular si NO vienen los valores
          if (item_tax === 0 && price_amount === 0) {
            if (taxPercent > 0) {
              const baseUnit = +(precio / (1 + taxPercent / 100)).toFixed(4);
              price_amount = baseUnit;
              item_tax = +(precio - baseUnit).toFixed(4);
            } else {
              price_amount = precio;
              item_tax = 0;
            }
          }
          items.push({
            id_pos: storeCode,
            sku: it.sku || it.item_code || it.code || "",
            ean: it.ean || it.barcode || it.sku || it.item_code || "",
            nombre: it.nombre || it.item_concept || it.description || "Item",
            categoria: it.categoria || it.category_code || "",
            grupo: it.grupo || null,
            marca: it.marca || it.brand || "",
            refe: it.refe || it.reference || it.item_reference || "",
            color: it.color || "",
            talla: it.talla || "",
            cantidad,
            costo: it.costo || it.cost || "0.0000",
            precio,
            precioB: it.precioB || "0.0000",
            precioC: it.precioC || "0.0000",
            descuento: it.descuento || it.discount || "0.0000",
            tax: (taxPercent || 0).toFixed(4),
            id_proveedor: it.id_proveedor || it.provider_id || "0001",
            store_name: seller.PartyName || "",
            category: it.category || it.categoria || "",
            groupName: it.groupName || null,
            brand: it.brand || it.marca || null,
            colorName: it.colorName || null,
            note: it.item_note || it.note || "",
            isExpanded: false,
            discount: it.discount || 0,
            price_amount: +price_amount.toFixed(2),
            item_tax: +item_tax.toFixed(2),
          });
        });
      });
      const total = items.reduce((s, it) => s + it.precio * it.cantidad, 0);
      this.checkoutDraft = {
        buyer,
        doc: {
          doc_type: "FE",
          doc_value: Math.round(total),
          store: storeCode,
          userId: profile.email || firstDoc.userId || "",
          vendor: profile.email || firstDoc.vendor || "",
        },
        items,
        payments: [],
        seller: { ...seller },
        store: storeCode,
      };
      console.log("Checkout Draft:", this.checkoutDraft);
      this.checkoutTotals.total = Math.round(total);
      this.checkoutTotals.unpaid = Math.round(total);
      this.payment_dialogo = true;
      this.docs_reference = group.docs.map((d) => d.doc_number);
    },
    handleDocTypeChanged(e) {
      if (this.checkoutDraft && e && e.doctype) {
        this.checkoutDraft.doc.doc_type = e.doctype;
      }
    },
    handleValidationUpdated(e) {
      if (!e || !Array.isArray(e.validations)) return;
      const next = e.validations;
      const cur = this.checkoutValidations;
      let changed = cur.length !== next.length;
      if (!changed) {
        for (let i = 0; i < cur.length; i++) {
          if (cur[i].status !== next[i].status) {
            changed = true;
            break;
          }
        }
      }
      if (changed) {
        this.checkoutValidations = next;
      }
    },
    handleShowMessage(msg) {
      if (!msg) return;
      this.$store.commit("setSnackbar", msg);
    },
    handleProcessPayment(data) {
      if (!data || !data.payment) return;
      const p = data.payment;
      this.checkoutDraft.payments.push({
        payment_concept: p.payment_concept || p.payment_reference || "PAGO",
        payment_code: p.payment_code || p.pay_mode || "01",
        payment_reference: p.payment_reference || p.payment_concept || "",
        payment_value: p.payment_value,
      });
      const paid = this.checkoutDraft.payments.reduce((s, x) => s + x.payment_value, 0);

      this.checkoutDraft.docs_reference = this.docs_reference;
      this.checkoutTotals.unpaid = Math.max(0, this.checkoutTotals.total - paid);
      if (this.checkoutTotals.unpaid === 0) {
        this.submitInvoice();
      }
    },
    submitInvoice() {
      console.log("[Cobrar] Submitting invoice draft:", this.checkoutDraft);
      if (this.postingInvoice || !this.checkoutDraft) return;
      // Clonar para no mutar referencia reactiva durante envío
      const invoice = JSON.parse(JSON.stringify(this.checkoutDraft));
      invoice.doc.doc_value = this.checkoutTotals.total; // asegurar valor final
      this.postingInvoice = true;
      this.$store.commit("setSnackbar", { text: "Enviando documento...", color: "info" });
      webserver("post_invoice_cm", invoice, (data) => {
        this.postingInvoice = false;
        console.log("[Cobrar] post_invoice_cm response:", data);

        // Caso 1: error de red del wrapper webserver
        if (!data || data.error === true) {
          this.$store.commit("setSnackbar", {
            text:
              "Error de red al cobrar: " +
              ((data && data.message) || "respuesta vacía"),
            color: "error",
          });
          return;
        }

        // Caso 2: rechazo del backend (string error)
        if (typeof data.error === "string") {
          this.$store.commit("setSnackbar", {
            text: "Backend rechazó la FE: " + data.error,
            color: "error",
          });
          console.warn("[Cobrar] Backend rechazó:", data.error, "Payload:", invoice);
          return;
        }

        // Caso 3: respuesta válida — la estructura es {document, buyer, items, ...}
        const savedDoc = data.document || {};
        if (!savedDoc.doc_number) {
          this.$store.commit("setSnackbar", {
            text: "Respuesta sin doc_number en data.document — revisar backend",
            color: "error",
          });
          console.warn("[Cobrar] Respuesta inesperada:", data);
          return;
        }

        console.log(
          "[Cobrar] FE creada — doc_type:",
          savedDoc.doc_type,
          "doc_number:",
          savedDoc.doc_number,
          "issuedate:",
          savedDoc.issuedate
        );

        // Liberar la mesa en localStorage si era una mesa
        if (this.cobrandoMesaId) {
          this.afterMesaCobrada(this.cobrandoMesaId);
          this.cobrandoMesaId = null;
        }

        // Abrir el visor con la respuesta normalizada
        this.viewerItem = this.normalizeViewerItem(data);
        this.newInvoiceFlag = true;
        this.viewerDialog = true;

        // Cerrar checkout y limpiar
        this.payment_dialogo = false;
        this.resetCheckoutState();

        this.$store.commit("setSnackbar", {
          text: `FE ${savedDoc.doc_number} creada — comandas cobradas`,
          color: "success",
        });

        // Recargar documentos para reflejar la nueva FE y CMs marcados
        this.loadDocs();
      });
    },

    // Cuando get_documents_by_day no trae CMs pendientes, usar get_dataTable como fallback
    // y poblar this.docs directamente con los pendientes (doc_reference NULL/'' y status != ANULADA)
    diagnosticarCMsDelDia() {
      const storeId = window.store && window.store.store_id;
      if (!storeId) return;
      // Query: TODOS los CMs de hoy (incluye cobrados)
      webserver(
        "get_dataTable",
        {
          store: storeId,
          table: "docs",
          filters: [
            { field: "doc_type", operator: "=", value: "CM" },
            { field: "DATE(issuedate)", operator: "=", value: this.issueDate },
            { field: "store", operator: "=", value: storeId },
          ],
        },
        (cmsHoy) => {
          if (Array.isArray(cmsHoy) && cmsHoy.length > 0) {
            const pendientes = cmsHoy.filter(
              (d) => !d.doc_reference && d.doc_status !== "ANULADA"
            );
            console.log(
              `[Board] Fallback get_dataTable: ${cmsHoy.length} CMs totales, ${pendientes.length} pendientes`
            );

            if (pendientes.length > 0) {
              // POBLAR el board con los pendientes (esto sí los muestra en pantalla)
              this.docs = pendientes;
              this.totalDocuments = pendientes.length;
              this.filterDocs();

              // Pre-cargar items de cada CM para que el botón "Cobrar" se active
              pendientes.forEach((d) => {
                if (d.doc_number && !this.docItems[d.doc_number]) {
                  this.loadItems(d);
                }
              });
              console.log(
                `[Board] Cargados ${pendientes.length} CMs pendientes vía fallback. Pre-cargando items...`
              );
            }
            return;
          }
          // No hay CMs hoy. Buscar últimos 5 CMs de cualquier fecha
          webserver(
            "get_dataTable",
            {
              store: storeId,
              table: "docs",
              filters: [
                { field: "doc_type", operator: "=", value: "CM" },
                { field: "store", operator: "=", value: storeId },
              ],
              order: "issuedate DESC",
              limit: 5,
            },
            (recentCMs) => {
              if (Array.isArray(recentCMs) && recentCMs.length > 0) {
                const fechas = [
                  ...new Set(
                    recentCMs.map((d) => (d.issuedate || "").split(" ")[0])
                  ),
                ];
                console.log(
                  `[Diag] Sin CMs hoy. Últimos 5 CMs de la tienda en fechas:`,
                  fechas
                );
                console.log(
                  "[Diag] Primer CM reciente:",
                  recentCMs[0].doc_number,
                  "fecha:",
                  recentCMs[0].issuedate,
                  "doc_reference:",
                  recentCMs[0].doc_reference
                );
              } else {
                console.log(
                  "[Diag] La tienda no tiene CMs en absoluto. Crea uno desde /InvoicePOS → Sin Pago → seleccionar mesa."
                );
              }
            }
          );
        }
      );
    },

    // Liberar la mesa tras cobrar exitoso: vuelve a available y limpia totalAmount
    afterMesaCobrada(mesaId) {
      if (!mesaId || !mesaId.toString().toLowerCase().startsWith("mesa_")) return;
      try {
        const storeId = window.store && window.store.store_id;
        if (!storeId) return;
        const key = `tables_${storeId}`;
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const tables = JSON.parse(raw);
        const idx = tables.findIndex((t) => t.id === mesaId);
        if (idx === -1) return;
        tables[idx] = {
          ...tables[idx],
          status: "available",
          hasOrders: false,
          totalAmount: 0,
          startTime: null,
        };
        localStorage.setItem(key, JSON.stringify(tables));
        console.log("[Cobrar] Mesa liberada en localStorage:", mesaId);
      } catch (e) {
        console.warn("[Cobrar] No se pudo liberar mesa:", e);
      }
    },
    resetCheckoutState() {
      this.checkoutDraft = null;
      this.checkoutTotals = { total: 0, unpaid: 0 };
      this.resetCheckoutValidations();
    },
    normalizeViewerItem(data) {
      if (!data) return { document: null, items: [], payments: [] };
      if (data.document) return data; // ya con estructura correcta
      return {
        document: data.doc || data.document || {},
        items: data.items || [],
        payments: data.payments || [],
      };
    },
    closeViewer() {
      this.viewerDialog = false;
      this.newInvoiceFlag = false;
    },
    handleOpenClientDialog() {
      if (this.checkoutDraft && this.checkoutDraft.buyer) {
        this.buyerEdit = { ...this.checkoutDraft.buyer };
      } else {
        this.buyerEdit = {
          PartyIdentification: "",
          PartyName: "",
          typeid: "CC",
          AddressLine: "",
          client_email: "",
          client_phone: "",
          CityName: "",
        };
      }
      this.clientEditDialog = true;
    },
    saveClientEdit() {
      if (!this.checkoutDraft) return;
      this.checkoutDraft = { ...this.checkoutDraft, buyer: { ...this.buyerEdit } };
      this.clientEditDialog = false;
      // eslint-disable-next-line no-console
      console.log("CLIENT_UPDATED", this.checkoutDraft.buyer);
    },
    resetCheckoutValidations() {
      this.checkoutValidations = [
        { status: false, label: "Nombre del cliente requerido" },
        { status: true, label: "Correo electrónico es opcional" },
        { status: false, label: "Documento de identificación requerido" },
        { status: false, label: "Tipo de documento requerido" },
        { status: true, label: "Vendedor requerido" },
        { status: false, label: "Requiere Factura Electrónica" },
        { status: false, label: "Valor" },
        { status: false, label: "Ciudad requerida" },
      ];
    },
  },
};
</script>

<style scoped>
.tickets-grid {
  position: relative;
  flex: 1 1 auto;
  overflow: auto;
  padding: 4px;
  border: 1px solid #eee;
  border-radius: 4px;
  background: linear-gradient(180deg, #fdfdfd, #f5f6f7);
}
.group-card {
  border-left: 5px solid #90a4ae;
  transition: all 0.2s ease;
  cursor: pointer;
}
.group-card {
  border-left: 5px solid #90a4ae;
  transition: box-shadow 0.15s;
  width: 100%;
  display: flex;
  flex-direction: column;
}
.group-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.14);
  transform: translateY(-2px);
}
.group-card-title {
  font-size: 13px;
}
.group-name {
  font-weight: 600;
  font-size: 13px;
}
.doc-outer {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.docs-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.doc-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11.5px;
  background: #fafbfc;
  border: 1px solid #eceff1;
  border-radius: 4px;
  padding: 3px 6px;
  transition: all 0.15s ease;
}
.doc-row:hover {
  background: #f5f5f5;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.doc-row.ok {
  border-left: 4px solid #4caf50;
}
.doc-row.pending {
  border-left: 4px solid #ff9800;
}
.doc-row .left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.doc-row .right {
  display: flex;
  align-items: center;
}
.doc-row .doc-number {
  font-weight: 600;
  cursor: pointer;
}
.doc-row .doc-number:hover {
  text-decoration: underline;
}
.doc-row .time {
  color: #555;
  font-size: 11px;
}
.doc-row .invoice {
  color: #666;
  font-size: 11px;
}
.doc-row .value {
  font-weight: 600;
  font-size: 11.5px;
  margin-right: 4px;
}
.type-chip {
  min-width: 32px;
  justify-content: center;
}
.toggle-btn {
  margin-left: -4px;
}
.items-wrapper {
  margin: 2px 0 6px 28px;
  border-left: 2px dashed #cfd8dc;
  padding-left: 8px;
}
.items-loading {
  font-size: 11px;
  display: flex;
  align-items: center;
}
.items-empty {
  font-size: 11px;
}
.items-v-list {
  background: transparent;
  padding: 0;
}
.item-row {
  min-height: 30px;
}
.item-line {
  font-size: 11.5px;
  font-weight: 500;
}
.item-sub {
  font-size: 10.5px;
}
.item-total {
  font-size: 11.5px;
  font-weight: 600;
}
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: 400px;
  color: #666;
}
.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  z-index: 10;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
@media (max-width: 700px) {
  .doc-row .value {
    font-size: 16px;
  }
}
</style>
